import { gsap } from 'gsap';
import Signal from '../../assets/scripts/classes/Signal';
import PageScroll from '../../assets/scripts/helpers/Scroll';

const HTML_CLASSLIST = document.documentElement.classList;

class Popups {
	constructor() {
		this.init();
	}

	init() {
		this.wrappers = document.querySelectorAll('[data-popups]');

		if (!this.wrappers.length) return;

		this.onOpen = new Signal();
		this.onClose = new Signal();
		this.onCloseStart = new Signal();

		this.opened = false;
		this.openedClass = '';
		this.activePopup = null;
		this.activePopupName = '';
		this.activeWrapper = null;
		this.popups = document.querySelectorAll('[data-popup]');

		const slideInAnimation = (popup, wrapper, completeHandler) => {
			gsap.set(wrapper, {
				display: 'flex',
				autoAlpha: 0,
				pointerEvents: 'auto',
			});

			gsap.set(popup, {
				display: 'flex',
				transformPerspective: 1500,
				y: 30,
				scale: 1,
				rotateX: -7,
				autoAlpha: 0,
				pointerEvents: 'auto',
			});

			gsap
				.timeline({
					onComplete: completeHandler,
					onStart: () => wrapper.classList.add('_active'),
				})
				.to(
					wrapper,
					{
						duration: 0.2,
						autoAlpha: 1,
						ease: 'power2.out',
					},
					0,
				)
				.to(
					popup,
					{
						duration: 0.2,
						y: 0,
						scale: 1,
						rotateX: 0,
						autoAlpha: 1,
						ease: 'cubic-bezier(0.24, 1.56, 0.64, 1)',
					},
					0.1,
				);
		};

		const slideOutAnimation = (popup, wrapper, immediate, completeHandler) => {
			wrapper.classList.remove('_active');

			const duration = immediate ? 0.1 : 0.2;

			gsap.set(popup, {
				transformPerspective: 1500,
			});

			gsap
				.timeline({
					onComplete: () => {
						gsap.set([popup, wrapper], {
							display: 'none',
							pointerEvents: 'none',
						});
						completeHandler();
					},
				})
				.to(
					popup,
					{
						duration,
						y: 30,
						scale: 0.98,
						rotateX: -7,
						autoAlpha: 0,
						ease: 'cubic-bezier(0.24, 1.56, 0.64, 1)',
					},
					0,
				)
				.to(
					wrapper,
					{
						duration: duration - 0.1,
						autoAlpha: 0,
						ease: 'power2.in',
					},
					immediate ? 0 : 0.1,
				);
		};

		this.animations = {
			slideIn: slideInAnimation,
			slideOut: slideOutAnimation,
		};

		document.querySelectorAll('[data-popup-opener]').forEach((element) => {
			element.addEventListener('click', (e) => {
				e.preventDefault();
				this.open(e.currentTarget.getAttribute('data-popup-opener'));
			});
		});

		document.querySelectorAll('[data-popup-closer]').forEach((element) => {
			element.addEventListener('click', (e) => {
				if (e.target.tagName !== 'A') e.preventDefault();
				this.close();
			});
		});

		window.addEventListener('keydown', (e) => {
			if (this.opened && e.key === 'Escape') {
				this.close();
			}
		});

		window.addEventListener('popstate', () => {
			this.close();
		});

		// Добавляем обработчики для всех контейнеров
		this.wrappers.forEach((wrapper) => {
			wrapper.addEventListener('click', (e) => {
				if (this.opened && e.target === wrapper) {
					this.close();
				}
			});
		});
	}

	open(name) {
		if (this.activePopupName === name || this.animating) return;

		if (this.opened) {
			this.close(true);
		}

		// Ищем popup во всех контейнерах
		let popup = null;
		let wrapper = null;

		for (const currentWrapper of this.wrappers) {
			popup = currentWrapper.querySelector(`[data-popup="${name}"]`);
			if (popup) {
				wrapper = currentWrapper;
				break;
			}
		}

		if (!popup) {
			console.warn(`No popup for opener: ${name}`);
			return;
		}

		this.opened = true;
		this.activePopup = popup;
		this.activePopupName = name;
		this.activeWrapper = wrapper;
		this.popupOpenAnimation = popup.getAttribute('data-popup-open-animation') || 'slideIn';

		wrapper.classList.add(`_${name}`);
		wrapper.classList.remove('no-pe');

		const completeHandler = () => {
			const focusEl = popup.querySelector('[data-popup-focus]');
			if (focusEl?.focus) focusEl.focus();

			this.animating = false;
			HTML_CLASSLIST.remove('_popup-animating');
		};

		this.animating = true;
		HTML_CLASSLIST.add('_popup-animating');

		const animateIn = this.animations[this.popupOpenAnimation];
		if (animateIn) {
			animateIn(popup, wrapper, completeHandler);
		} else {
			gsap.fromTo(
				popup,
				{ autoAlpha: 0, scale: 0.98, display: 'flex' },
				{
					duration: 0.2,
					autoAlpha: 1,
					scale: 1,
					clearProps: 'transform',
					onStart: () => gsap.set(wrapper, { display: 'flex', autoAlpha: 1 }),
					onComplete: completeHandler,
				},
			);
		}

		HTML_CLASSLIST.add('_popup-opened');
		this.openedClass = `_popup-opened-${name}`;
		HTML_CLASSLIST.add(this.openedClass);

		if (!HTML_CLASSLIST.contains('_modal-opened')) {
			PageScroll.disabled();
		}
		this.onOpen.call(name);

		if (name === 'authorize') {
			return;
		}

		window.history.replaceState({}, '', `#${name}`);
	}

	close(immediate = false) {
		if (this.animating || !this.opened) return;

		this.opened = false;
		this.activeWrapper.classList.remove(`_${this.activePopupName}`);
		this.activeWrapper.classList.add('no-pe');
		this.onCloseStart.call(this.activePopupName);

		const popup = this.activePopup;
		const wrapper = this.activeWrapper;
		const closeAnimation = popup.getAttribute('data-popup-close-animation') || 'slideOut';

		this.activePopupName = '';
		this.activePopup = null;
		this.activeWrapper = null;

		const completeHandler = () => {
			this.animating = false;
			if (!HTML_CLASSLIST.contains('_modal-opened')) {
				PageScroll.enabled();
			}
			HTML_CLASSLIST.remove('_popup-animating');
			this.onClose.call();
		};

		this.animating = true;
		HTML_CLASSLIST.add('_popup-animating');

		const animateOut = this.animations[closeAnimation];
		if (animateOut) {
			animateOut(popup, wrapper, immediate, completeHandler);
		} else {
			gsap.to(popup, {
				duration: immediate ? 0 : 0.2,
				autoAlpha: 0,
				scale: 0.98,
				display: 'none',
				clearProps: 'transform',
				onComplete: completeHandler,
			});
		}

		HTML_CLASSLIST.remove('_popup-opened');
		if (this.openedClass) {
			HTML_CLASSLIST.remove(this.openedClass);
			this.openedClass = '';
		}

		window.history.replaceState('', document.title, window.location.pathname + window.location.search);
	}

	getPopup(name) {
		for (const wrapper of this.wrappers) {
			const popup = wrapper.querySelector(`[data-popup="${name}"]`);
			if (popup) {
				return popup;
			}
		}
		return null;
	}
}

export default new Popups();
